/**
 * Forgot Password page
 */

import {useState, FormEvent} from 'react';
import {Link} from 'react-router-dom';
import {sendPasswordResetEmail} from 'firebase/auth';
// getFirebaseAuth imported dynamically when needed
import {ROUTES} from '../config/constants';
import {getErrorMessage} from '../utils/authErrors';
import {Button} from '../components/ui/Button';
import {Input} from '../components/ui/Input';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handlePasswordReset = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!email) {
      setError('Please enter your email address');
      return;
    }

    try {
      setIsLoading(true);

      // FIXED: Ensure Firebase is initialized before using auth
      const {getFirebaseAuthAsync} = await import('@voxapilot/shared');
      const auth = await getFirebaseAuthAsync();
      await sendPasswordResetEmail(auth, email);
      
      setSuccess(true);
      setIsLoading(false);
      
      // Clear email after success
      setTimeout(() => {
        setEmail('');
      }, 3000);
    } catch (err: any) {
      // Use error message utility for user-friendly messages
      const errorMessage = getErrorMessage(err);
      setError(errorMessage);
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Reset your password
          </h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Enter your email address and we'll send you a link to reset your password.
          </p>
        </div>

        {/* Reset Card */}
        <div className="bg-white dark:bg-gray-800 shadow-lg rounded-lg p-8 space-y-6">
          {success ? (
            <div className="space-y-4">
              <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                <p className="text-sm text-green-800 dark:text-green-200 text-center">
                  Password reset email sent! Please check your inbox and follow the instructions.
                </p>
              </div>
              <Link to={ROUTES.SIGN_IN}>
                <Button variant="primary" className="w-full">
                  Back to Sign in
                </Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handlePasswordReset} className="space-y-4">
              <div>
                <Input
                  type="email"
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                  error={!!error}
                  autoComplete="email"
                  required
                />
              </div>

              {error && (
                <div className="text-sm text-red-600 dark:text-red-400 text-center">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                variant="primary"
                className="w-full"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Sending...
                  </>
                ) : (
                  'Send reset email'
                )}
              </Button>
            </form>
          )}

          {/* Footer Links */}
          <div className="text-center text-sm">
            <Link
              to={ROUTES.SIGN_IN}
              className="text-orange-600 hover:text-orange-500 dark:text-orange-400 dark:hover:text-orange-300 font-medium"
            >
              Back to Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

